import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Image } from "@caspeco/casper-ui-library.components.image";
import {
	ThemeSpaceVariable,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { Stack } from "@caspeco/casper-ui-library.components.stack";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { Button } from "@caspeco/casper-ui-library.components.button";
import { Divider } from "@caspeco/casper-ui-library.components.divider";
import { useMinWidthMediaQuery } from "hooks/useMediaQuery";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import PlaceSkeleton from "components/skeletons/PlaceSkeleton";
import { getLocalStorage } from "helpers/local-storage-helper/local-storage-helper";
import { IAppGeneralFeatures } from "types/appFeatures";
import { CSSVarColumnWidthLarge } from "components/global-styles/GlobalStyles";
import { AspectRatioWrapper } from "components/aspect-ratio-wrapper/AspectRatioWrapper";
import { usePlaceStore } from "store/placeStore";
import { useDeliveryOptionsStore } from "store/deliveryOptionsStore";
import { createBSTLSearchParams } from "helpers/search-param-helper/search-param-helper";
import {
	getAutoSelectableDeliveryNode,
	getDeliveryTypeTitle,
	getIconForDeliveryType,
	hasNodes,
	isDeliveryTypeThatCanHaveNodes,
} from "helpers/deliveryoptions-helper/deliveryoptions-helper";
import { DeliveryType } from "types/deliveryOption";
import { BreakPoint, AspectRatio } from "types/ui";
import PlaceContactInfo from "components/place-contact-info/PlaceContactInfo";
import OpeningHours from "components/opening-hours/OpeningHours";
import NotFound from "components/not-found/NotFound";
import DeliveryOptionsModal from "components/delivery-options-modal/DeliveryOptionsModal";
import { useViewType } from "helpers/view-type-helper/view-type-helper";
import { usePlacePageRequests } from "../hooks/usePlacePageRequests";

export default function Place() {
	const { appAlias } = useParams<"appAlias">();
	const [, setSearchParams] = useSearchParams();
	const isLargerScreen = useMinWidthMediaQuery(BreakPoint.Medium);
	const { rootDeliveryOption } = useDeliveryOptionsStore();
	const { place } = usePlaceStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isOpen, onOpen, onClose } = useDisclosure({
		onClose: () => setSearchParams({}),
	});
	const { isLoading, error } = usePlacePageRequests();
	const view = useViewType();

	const navigateWithDeliveryOptions = (
		deliveryOptionType: DeliveryType | undefined,
		deliveryOptionId?: string,
	): void => {
		let pathname;

		// Check if app features include showMenuSectionList,
		// should then be appended to search params at this point
		const hasShowMenuListFeature =
			getLocalStorage<IAppGeneralFeatures>("appFeatures")?.showMenuSectionList ?? false;

		const search = createBSTLSearchParams(
			deliveryOptionType,
			deliveryOptionId,
			hasShowMenuListFeature,
			view,
		).toString();

		// Find menus (their ids) that support the selected delivery type / method
		const menus = place?.menuIdsPerSupportedDeliveryType.find(
			(menuMap) => menuMap.deliveryType === deliveryOptionType,
		)?.menuIds;

		if (menus && menus?.length === 1) {
			pathname = `/${appAlias}/${place?.id}/${menus?.[0]}`;
		} else {
			pathname = `/${appAlias}/${place?.id}/menus`;
		}
		// Go to new url based on selection
		navigate({ pathname, search });
	};

	const onClickDeliveryTypeButton = (type: DeliveryType) => {
		const autoSelectableNode = getAutoSelectableDeliveryNode(rootDeliveryOption, type);

		const isRequiredToMakeNodeSelection =
			isDeliveryTypeThatCanHaveNodes(type) && hasNodes(rootDeliveryOption) && !autoSelectableNode;

		if (isRequiredToMakeNodeSelection) {
			// Apply type to search params, used in delivery option modal to display options
			setSearchParams(createBSTLSearchParams(type));
			onOpen();
		} else {
			navigateWithDeliveryOptions(type, autoSelectableNode?.id);
		}
	};

	if (isLoading) return <PlaceSkeleton />;
	if (error) return <NotFound showWithButton />;
	return (
		<>
			<DeliveryOptionsModal
				onClose={onClose}
				isOpen={isOpen}
				deliveryTypes={["EAT_AT_RESTAURANT"]}
				isOpenedFromPlaceRoute={true}
				onSubmit={navigateWithDeliveryOptions}
			/>
			<Flex justify="center">
				<Box
					w={{ base: "100%", md: CSSVarColumnWidthLarge }}
					display="flex"
					justifyContent="center"
					mb={{ md: ThemeSpaceVariable.XLarge }}
					color={ThemeColorVariable.OnBackground}
					pb={ThemeSpaceVariable.Large}
				>
					<Box display="flex" flexDirection="column" w="100%" mt={{ md: ThemeSpaceVariable.XLarge }}>
						{place?.imageMap.hasImage(AspectRatio.SixteenByNine) && (
							<Image
								fallback={
									<AspectRatioWrapper $aspectratio={AspectRatio.SixteenByNine}>
										<Box
											w="100%"
											bg={ThemeColorVariable.BackgroundDisabled}
											borderRadius={{
												base: ThemeRadiusVariable.None,
												sm: ThemeRadiusVariable.Medium,
											}}
										/>
									</AspectRatioWrapper>
								}
								alt={t("misc_picture_of_x", {
									0: place?.name,
								})}
								borderRadius={isLargerScreen ? ThemeRadiusVariable.Medium : "none"}
								src={place.imageMap.withSizeQuery(AspectRatio.SixteenByNine, "l")}
								srcSet={place.imageMap.getScrSet(AspectRatio.SixteenByNine, "l", "xl")}
							/>
						)}
						<Box
							py={ThemeSpaceVariable.Medium}
							px={{
								base: ThemeSpaceVariable.Medium,
								md: ThemeSpaceVariable.None,
							}}
						>
							<Box mb={ThemeSpaceVariable.Medium}>
								<Heading
									color={ThemeColorVariable.OnBackgroundHeader}
									as="h2"
									fontFamily={ThemeFontVariable.SubHeader}
									fontWeight="medium"
									mb={ThemeSpaceVariable.Small}
								>
									{place?.name}
								</Heading>
								<Text
									fontSize={ThemeFontSizeVariable.Large}
								>{`${place?.address.streetAddress}, ${place?.address.city}`}</Text>
							</Box>
							<Stack divider={<Divider borderColor={ThemeColorVariable.OnBackgroundBorder} />} spacing="32px">
								<Stack spacing={ThemeSpaceVariable.Medium} direction={{ base: "column", md: "row" }}>
									{place?.supportedDeliveryTypes.map((type, i) => {
										const title = getDeliveryTypeTitle(type);
										const icon = getIconForDeliveryType(type);
										const onClick = () => onClickDeliveryTypeButton(type);
										return (
											<Button key={i} size="lg" w="100%" variant="primary" onClick={onClick} leftIcon={icon}>
												{title}
											</Button>
										);
									})}
								</Stack>
								{place && (
									<OpeningHours
										headerColor={ThemeColorVariable.OnBackgroundHeader}
										openingHours={place?.openingHours ?? []}
										timeZoneName={place?.timeZoneName}
									/>
								)}
								{place?.description && (
									<Box as="section">
										<Heading
											as="h3"
											fontFamily={ThemeFontVariable.SubHeader}
											color={ThemeColorVariable.OnBackgroundHeader}
											fontWeight="medium"
											fontSize={ThemeFontSizeVariable.Large}
											mb={ThemeSpaceVariable.Small}
										>
											{t("misc_about_x", { 0: place?.name })}
										</Heading>
										<Text>{place.description}</Text>
									</Box>
								)}
								{place && (
									<PlaceContactInfo
										headerColor={ThemeColorVariable.OnBackgroundHeader}
										phone={place.phone}
										email={place.email}
										corporateId={place.corporateId}
									/>
								)}
							</Stack>
						</Box>
					</Box>
				</Box>
			</Flex>
		</>
	);
}
